import * as pdfjsLib from 'pdfjs-dist';
import { useEffect, useState } from 'react';

type ThumbnailsMap = Map<number, HTMLCanvasElement>;
type ThumbnailsResult = {
  pdfDocument: pdfjsLib.PDFDocumentProxy;
  thumbnails: ThumbnailsMap;
};

const THUMBNAIL_SCALE = 0.15;
const NO_THUMBNAILS: ThumbnailsMap = new Map();

export function usePdfThumbnails(
  pdfDocument: pdfjsLib.PDFDocumentProxy | null,
  totalPages: number,
) {
  const [result, setResult] = useState<ThumbnailsResult | null>(null);

  useEffect(() => {
    if (!pdfDocument || totalPages === 0) {
      return;
    }

    let cancelled = false;

    const generateThumbnails = async () => {
      const thumbnailMap: ThumbnailsMap = new Map();

      for (let pageNumber = 1; pageNumber <= totalPages; pageNumber++) {
        if (cancelled) return;
        const page = await pdfDocument.getPage(pageNumber);
        const thumbnail = await renderThumbnail(page);
        thumbnailMap.set(pageNumber, thumbnail);
      }

      if (!cancelled) setResult({ pdfDocument, thumbnails: thumbnailMap });
    };

    generateThumbnails().catch(() => {
      // Renders in flight fail once the document is destroyed
      if (cancelled || pdfDocument.loadingTask.destroyed) return;
      setResult({ pdfDocument, thumbnails: NO_THUMBNAILS });
    });

    return () => {
      cancelled = true;
    };
  }, [pdfDocument, totalPages]);

  const isCurrentResult = result !== null && result.pdfDocument === pdfDocument;

  return {
    thumbnails: isCurrentResult ? result.thumbnails : NO_THUMBNAILS,
    isLoading: pdfDocument === null || (totalPages > 0 && !isCurrentResult),
  };
}

async function renderThumbnail(
  page: pdfjsLib.PDFPageProxy,
): Promise<HTMLCanvasElement> {
  const viewport = page.getViewport({ scale: THUMBNAIL_SCALE });

  const canvas = document.createElement('canvas');
  canvas.width = viewport.width;
  canvas.height = viewport.height;

  const context = canvas.getContext('2d')!;
  await page.render({ canvasContext: context, viewport, canvas }).promise;

  return canvas;
}
